import {
  Briefcase,
  Building2,
  IndianRupee,
  Wallet,
  BadgePercent,
  RefreshCcw,
  Clock3,
  ShieldCheck,
  FileText,
  TrendingUp,
  Landmark,
  CircleDollarSign,
  User,
  Users,
} from "./icons";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

const schemas = [
  websiteSchema(),
  organizationSchema(),
  financialServiceSchema(),
  localBusinessSchema(),
  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Overdraft & Dropline OD",
      path: "/overdraft-dropline-od",
    },
  ]),
];

/* -------------------------------------------------------------------------- */
/*                                    SEO                                     */
/* -------------------------------------------------------------------------- */

export const seo = {
  title: "Overdraft & Dropline OD Facility | Money Mart",

  description:
    "Manage your business cash flow with an Overdraft or Dropline OD Facility from leading Banks & NBFCs. Get flexible withdrawals, pay interest only on the utilized amount, and receive expert assistance from Money Mart.",

  keywords:
    "Overdraft Facility, Dropline OD, OD Loan, Business Overdraft, Working Capital OD, Cash Credit Alternative, Money Mart",

  canonical: "/overdraft-dropline-od",

  structuredData: { schemas },
};

/* -------------------------------------------------------------------------- */
/*                                    Hero                                    */
/* -------------------------------------------------------------------------- */

export const hero = {
  badge: "Flexible Working Capital Solutions",

  title: "Access Funds Whenever Your Business Needs Them",

  description:
    "An Overdraft or Dropline OD Facility gives your business access to a pre-approved credit limit that can be withdrawn whenever required. Pay interest only on the amount utilized while maintaining healthy business cash flow.",

  highlights: [
    "Interest Only on Amount Utilized",
    "Flexible Withdrawals & Repayments",
    "Ideal for Working Capital Needs",
    "Quick Approval from Leading Banks & NBFCs",
  ],

  image: "/images/loans/overdraft_od.webp",

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

/* -------------------------------------------------------------------------- */
/*                                  Overview                                  */
/* -------------------------------------------------------------------------- */

export const overview = {
  badge: "Overdraft & Dropline OD",

  title: "A Flexible Credit Facility for Growing Businesses",

  description:
    "Unlike a traditional term loan, an Overdraft or Dropline OD Facility provides a revolving credit limit that you can use whenever required. It is an ideal financing solution for businesses looking to manage working capital, seasonal expenses, inventory purchases, and day-to-day operational costs.",

  items: [
    {
      icon: Wallet,
      title: "Withdraw as Needed",
      description:
        "Access funds whenever required instead of taking the entire sanctioned amount at once.",
    },
    {
      icon: BadgePercent,
      title: "Interest on Utilized Amount",
      description:
        "Pay interest only on the amount you actually use instead of the entire approved limit.",
    },
    {
      icon: RefreshCcw,
      title: "Flexible Repayments",
      description:
        "Deposit funds back into the account and reuse the available credit limit as per facility terms.",
    },
    {
      icon: TrendingUp,
      title: "Better Cash Flow Management",
      description:
        "Maintain smooth business operations by managing short-term cash flow fluctuations effectively.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                  Benefits                                  */
/* -------------------------------------------------------------------------- */

export const benefits = {
  badge: "Benefits",

  title: "Why Choose an Overdraft or Dropline OD Facility?",

  description:
    "Overdraft facilities provide businesses with financial flexibility while reducing borrowing costs through interest charged only on the utilized amount.",

  items: [
    {
      icon: CircleDollarSign,
      title: "Pay Only for What You Use",
      description:
        "Interest is charged only on the amount withdrawn instead of the entire sanctioned credit limit.",
    },
    {
      icon: Wallet,
      title: "Instant Access to Funds",
      description:
        "Withdraw funds whenever required without applying for a new loan every time.",
    },
    {
      icon: RefreshCcw,
      title: "Revolving Credit Facility",
      description:
        "Repay utilized funds and continue using the available credit as permitted under your sanctioned limit.",
    },
    {
      icon: Clock3,
      title: "Quick Access to Working Capital",
      description:
        "Meet urgent operational expenses, vendor payments, salary payouts, and inventory purchases with ease.",
    },
    {
      icon: ShieldCheck,
      title: "Supports Business Growth",
      description:
        "Maintain uninterrupted business operations without disrupting your cash reserves.",
    },
    {
      icon: FileText,
      title: "Multiple End Uses",
      description:
        "Use the facility for inventory, receivables, business expansion, seasonal demand, or general working capital requirements.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export const types = {
  badge: "Credit Facilities",

  title: "Overdraft Solutions We Help Arrange",

  description:
    "Money Mart helps businesses compare multiple working capital solutions from leading Banks and NBFCs based on their funding requirements.",

  items: [
    {
      icon: Landmark,
      title: "Overdraft Facility (OD)",
      description:
        "A revolving credit facility that allows businesses to withdraw and repay funds within an approved credit limit.",
    },
    {
      icon: TrendingUp,
      title: "Dropline Overdraft",
      description:
        "An overdraft facility where the sanctioned limit gradually reduces over the loan tenure according to a predefined schedule.",
    },
    {
      icon: Briefcase,
      title: "Business Working Capital OD",
      description:
        "Designed to finance day-to-day business operations including inventory, receivables, salaries, and operational expenses.",
    },
    {
      icon: Building2,
      title: "Secured Business OD",
      description:
        "Overdraft facilities backed by eligible collateral, offering higher credit limits and competitive interest rates.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                Eligibility                                 */
/* -------------------------------------------------------------------------- */

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your Overdraft & Dropline OD Eligibility",

  description:
    "Eligibility depends on your business profile, turnover, repayment capacity, banking relationship, and the lender's credit assessment.",

  requirements: [
    {
      icon: User,
      title: "Applicant",
      value:
        "Business Owner, Self-Employed Professional, Proprietorship, Partnership, LLP or Company",
    },
    {
      icon: Briefcase,
      title: "Business Vintage",
      value: "Stable Business Operations (as per lender norms)",
    },
    {
      icon: IndianRupee,
      title: "Business Income",
      value: "Regular & Verifiable Business Cash Flow",
    },
    {
      icon: Building2,
      title: "Security",
      value: "Secured or Unsecured Facility (Subject to Lender Policy)",
    },
  ],

  approvalFactors: [
    "Business turnover and profitability",
    "Banking relationship and account conduct",
    "Credit score & repayment history",
    "Business stability and vintage",
    "Existing liabilities",
    "Collateral value (for secured facilities)",
  ],

  cta: {
    title: "Not sure if your business qualifies?",

    description:
      "Our experts will assess your business profile and help you find the most suitable Overdraft or Dropline OD facility.",

    buttonText: "Check Eligibility Now",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Documents                                  */
/* -------------------------------------------------------------------------- */

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for an Overdraft or Dropline OD Facility",

  description:
    "The exact documentation varies depending on the lender and applicant profile. These are the documents commonly required for most Overdraft and Dropline OD applications.",

  groups: [
    {
      icon: Users,
      title: "Documents Required for All Applicants",

      documents: [
        "Passport Size Photograph",
        "PAN Card",
        "Latest Aadhaar Card",
      ],
    },
    {
      icon: Briefcase,
      title: "Business Documents",

      documents: [
        "GST Registration (if applicable)",
        "UDYAM Registration (if applicable)",
        "Business Registration Proof",
        "Partnership Deed / LLP Agreement / MOA & AOA (as applicable)",
      ],
    },
    {
      icon: Building2,
      title: "Financial Documents",

      documents: [
        "Last 3 Years Income Tax Returns",
        "Profit & Loss Statement",
        "Balance Sheet",
        "Latest Business Financial Statements",
      ],
    },
    {
      icon: Landmark,
      title: "Banking Documents",

      documents: [
        "Last 6–12 Months Business Bank Statements",
        "Existing Loan Details (if applicable)",
        "Working Capital Details (if applicable)",
      ],
    },
    {
      icon: ShieldCheck,
      title: "Collateral Documents (For Secured OD)",

      documents: [
        "Property Ownership Documents",
        "Latest Property Tax Receipt",
        "Property Valuation Documents (if required)",
        "Any Additional Security Documents Requested by the Lender",
      ],
    },
    {
      icon: FileText,
      title: "Additional Supporting Documents",

      documents: [
        "Business Address Proof",
        "KYC of Directors / Partners",
        "Any Additional Documents Requested by the Lender",
      ],
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Process                                  */
/* -------------------------------------------------------------------------- */

export const loanProcess = {
  ...process,

  title: "Get Your Overdraft Facility in 6 Simple Steps",

  description:
    "From business assessment and document verification to credit approval and limit sanction, our experts guide you throughout the entire process.",
};

/* -------------------------------------------------------------------------- */
/*                              Why Money Mart                                */
/* -------------------------------------------------------------------------- */

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Choose Money Mart for Your Overdraft Facility?",

  description:
    "We help businesses compare Overdraft and Dropline OD facilities from multiple Banks and NBFCs to secure competitive interest rates, higher limits, and faster approvals with expert guidance throughout the process.",

  items: whyMoneyMartItems,
};

/* -------------------------------------------------------------------------- */
/*                                     FAQ                                    */
/* -------------------------------------------------------------------------- */

export const faq = {
  badge: "Frequently Asked Questions",

  title: "Overdraft & Dropline OD FAQs",

  description:
    "Find answers to the most commonly asked questions about Overdraft and Dropline OD facilities.",

  items: [
    {
      question: "What is an Overdraft (OD) Facility?",

      answer:
        "An Overdraft Facility is a revolving line of credit that allows businesses to withdraw funds up to a sanctioned limit whenever required. Interest is charged only on the amount utilized.",
    },
    {
      question: "What is a Dropline Overdraft?",

      answer:
        "A Dropline Overdraft is similar to a regular overdraft, but the sanctioned credit limit gradually reduces over the tenure according to a predefined schedule while still providing flexible access to funds.",
    },
    {
      question: "What can an Overdraft Facility be used for?",

      answer:
        "Businesses commonly use Overdraft Facilities for working capital, inventory purchases, supplier payments, salary expenses, receivables management, and meeting short-term operational requirements.",
    },
    {
      question: "Do I pay interest on the full sanctioned limit?",

      answer:
        "No. Interest is generally charged only on the amount you actually withdraw and utilize, making an Overdraft Facility more flexible than a traditional term loan.",
    },
    {
      question: "Can I repay and withdraw funds multiple times?",

      answer:
        "Yes. Subject to the terms of your sanctioned facility, you can repay the utilized amount and withdraw funds again within your approved credit limit.",
    },
    {
      question: "Is collateral required for an Overdraft Facility?",

      answer:
        "Both secured and unsecured Overdraft Facilities are available. The eligibility, sanctioned limit, and interest rate depend on the lender's policies and your business profile.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                     CTA                                    */
/* -------------------------------------------------------------------------- */

export const cta = {
  badge: "Need Flexible Working Capital?",

  title: "Let's Find the Right Overdraft Facility for Your Business",

  description:
    "Compare Overdraft and Dropline OD facilities from leading Banks and NBFCs with expert guidance from Money Mart.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for Overdraft Facility",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Lead Form                                  */
/* -------------------------------------------------------------------------- */

export const leadForm = {
  badge: "Free Business Finance Consultation",

  title: "Speak to an Overdraft Facility Expert",

  description:
    "Our business finance specialists will compare multiple lenders and help you secure the most suitable Overdraft or Dropline OD Facility for your working capital requirements.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our business finance expert will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Business Type",
      income: "Annual Turnover",
      loanAmount: "Required Credit Limit",
    },

    incomeField: "annualTurnover",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-25L",
        label: "Below ₹25 Lakhs",
      },
      {
        value: "25-100L",
        label: "₹25 Lakhs – ₹1 Crore",
      },
      {
        value: "1-5Cr",
        label: "₹1 Crore – ₹5 Crore",
      },
      {
        value: "5-10Cr",
        label: "₹5 Crore – ₹10 Crore",
      },
      {
        value: "10Cr-plus",
        label: "Above ₹10 Crore",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your finance enquiry. No spam. No hidden charges.",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Export                                    */
/* -------------------------------------------------------------------------- */

const overdraftDroplineOD = {
  seo,
  hero,
  overview,
  benefits,
  types,
  eligibility,
  documents,
  process: loanProcess,
  whyMoneyMart,
  faq,
  cta,
  leadForm,
};

export default overdraftDroplineOD;
